import {
    FlexColumn,
    FlexRowBetween,
    LoaderBar,
    PageHeader,
} from 'components'
import { useAuth } from 'context'
import {
    GetConsolidatedReportRuns,
    GetConsolidatedReportScheduleList,
} from 'hooks'
import {
    ConsolidatedScheduleList,
    FireNowModal,
    NoRunsEmpty,
    NoSchedulesEmpty,
    NoSearchResultsEmpty,
    RunHistoryList,
    ViewRunDetailModal,
} from 'page-components/consolidated-report-email'
import { ConsolidatedToasts } from 'page-components/consolidated-report-email/consolidated-report-toasts'
import {
    canManageConsolidatedReports,
    exportRunsToCsv,
    extractApiError,
    formatFireWhen,
    RUN_STATUS_OPTIONS,
    STATUS_OPTIONS,
} from 'page-components/consolidated-report-email/consolidated-report-email-utils'
import { ScreenContainer, SectionCard, SectionHeader, FilterRow } from 'page-components/consolidated-report-email/consolidated-report-email-styles'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Input, Message } from 'semantic-ui-react'
import { AppDropdown as Dropdown } from 'components/AppDropdown'
import { AppPrimaryButton, AppButton } from 'elements'
import { ConsolidatedReportScheduleService, DefaultText } from 'utility'
import { DesktopMode, MobileMode } from 'layout'
import styled from 'styled-components'

const ListScreenContainer = styled(ScreenContainer)``

const getCompanyTimezone = user =>
    user?.Companies?.[0]?.TimeZone ||
    user?.TimeZone ||
    'Central Standard Time'

export const ConsolidatedReportEmailListScreen = () => {
    const navigate = useNavigate()
    const { user } = useAuth()
    const timezone = getCompanyTimezone(user)

    const {
        data: schedules,
        loading: schedulesLoading,
        error: schedulesError,
        filter,
        totalCount,
        pageChanged,
        filterChanged,
        refresh: refreshSchedules,
    } = GetConsolidatedReportScheduleList()

    const {
        data: runs,
        loading: runsLoading,
        error: runsError,
        filter: runsFilter,
        totalCount: runsTotalCount,
        pageChanged: runsPageChanged,
        filterChanged: runsFilterChanged,
        refresh: refreshRuns,
    } = GetConsolidatedReportRuns()

    const [search, setSearch] = useState('')
    const [statusFilter, setStatusFilter] = useState('all')
    const [runScheduleFilter, setRunScheduleFilter] = useState('all')
    const [runStatusFilter, setRunStatusFilter] = useState('all')
    const [fireNowSchedule, setFireNowSchedule] = useState(null)
    const [fireNowLoading, setFireNowLoading] = useState(false)
    const [fireNowError, setFireNowError] = useState('')
    const [selectedRun, setSelectedRun] = useState(null)

    const handleScheduleSearch = () => {
        filterChanged({
            search: search.trim(),
            status: statusFilter,
        })
    }

    const handleClearScheduleSearch = () => {
        setSearch('')
        setStatusFilter('all')
        filterChanged({ search: '', status: 'all' })
    }

    const scheduleOptions = useMemo(() => {
        const byId = new Map()
        const scheduleList = schedules || []
        const runList = runs || []

        scheduleList.forEach(s => {
            if (s?.ScheduleId != null) {
                byId.set(s.ScheduleId, s.ScheduleName || `Schedule #${s.ScheduleId}`)
            }
        })
        runList.forEach(r => {
            if (r?.ScheduleId != null && !byId.has(r.ScheduleId)) {
                byId.set(
                    r.ScheduleId,
                    r.ScheduleName || `Schedule #${r.ScheduleId}`,
                )
            }
        })
        return [
            { key: 'all', text: 'All schedules', value: 'all' },
            ...Array.from(byId.entries()).map(([id, name]) => ({
                key: id,
                text: name,
                value: id,
            })),
        ]
    }, [schedules, runs])

    const nextFire = useMemo(() => {
        const active = (schedules || []).filter(s => s.IsActive && !s.OnHold && s.NextRunUtc)
        if (!active.length) return null
        const sorted = [...active].sort(
            (a, b) => new Date(a.NextRunUtc) - new Date(b.NextRunUtc),
        )
        return {
            utc: sorted[0].NextRunUtc,
            name: sorted[0].ScheduleName,
            timezone,
        }
    }, [schedules, timezone])

    const filteredSchedules = useMemo(() => {
        const text = String(filter.search || '').trim().toLowerCase()
        const status = filter.status || 'all'

        return (schedules || []).filter(schedule => {
            const matchesSearch =
                !text ||
                String(schedule.ScheduleName || '')
                    .toLowerCase()
                    .includes(text)

            const matchesStatus =
                status === 'all' ||
                (status === 'held' && schedule.OnHold === true) ||
                (status === 'active' &&
                    schedule.IsActive === true &&
                    schedule.OnHold !== true)

            return matchesSearch && matchesStatus
        })
    }, [schedules, filter.search, filter.status])

    const filteredScheduleCount =
        filter.search || filter.status !== 'all'
            ? filteredSchedules.length
            : totalCount

    const filteredRuns = useMemo(() => {
        const selectedScheduleId =
            runsFilter.scheduleId && runsFilter.scheduleId !== 'all'
                ? Number(runsFilter.scheduleId)
                : null
        const selectedStatus = runsFilter.status || 'all'

        return (runs || []).filter(run => {
            const matchesSchedule =
                !selectedScheduleId ||
                Number(run.ScheduleId) === selectedScheduleId
            const matchesStatus =
                selectedStatus === 'all' || run.Status === selectedStatus

            return matchesSchedule && matchesStatus
        })
    }, [runs, runsFilter.scheduleId, runsFilter.status])

    const filteredRunsCount =
        runsFilter.scheduleId || runsFilter.status !== 'all'
            ? filteredRuns.length
            : runsTotalCount

    if (!canManageConsolidatedReports(user)) {
        return (
            <Message negative>
                You don&apos;t have permission to manage consolidated reports.
                This feature is available to company admin users only.
            </Message>
        )
    }

    const handleHoldToggle = async (schedule, onHold) => {
        try {
            await ConsolidatedReportScheduleService.hold(
                schedule.ScheduleId,
                onHold,
            )
            if (onHold) {
                ConsolidatedToasts.scheduleHeld()
            } else {
                ConsolidatedToasts.scheduleResumed(
                    schedule.NextRunUtc
                        ? formatFireWhen(schedule.NextRunUtc, timezone)
                        : '',
                )
            }
            refreshSchedules()
        } catch (err) {
            ConsolidatedToasts.genericError(
                onHold ? "Couldn't hold schedule" : "Couldn't resume schedule",
                extractApiError(err) || DefaultText.Default,
            )
        }
    }

    const handleFireNow = async reasonText => {
        if (!fireNowSchedule) return
        try {
            setFireNowLoading(true)
            setFireNowError('')
            const result = await ConsolidatedReportScheduleService.fireNow(
                fireNowSchedule.ScheduleId,
                reasonText,
            )
            ConsolidatedToasts.digestFired({
                recipients: result?.RecipientCount,
                reports: result?.ReportsIncluded,
                runId: result?.RunId,
                mode: result?.RenderMode,
                status: result?.Status,
            })
            setFireNowSchedule(null)
            refreshSchedules()
            refreshRuns()
        } catch (err) {
            const msg = extractApiError(err)
            setFireNowError(msg || 'Fire Now failed — see Run History for details.')
            ConsolidatedToasts.fireNowFailed(msg)
        } finally {
            setFireNowLoading(false)
        }
    }

    const handleExportCsv = () => {
        exportRunsToCsv(filteredRuns, timezone)
    }

    const showSearchEmpty =
        !schedulesLoading &&
        filteredSchedules.length === 0 &&
        (filter.search || filter.status !== 'all')

    const showFirstTimeEmpty =
        !schedulesLoading &&
        schedules?.length === 0 &&
        !filter.search &&
        filter.status === 'all'

    return (
        <ListScreenContainer>
            <DesktopMode>
                <FlexRowBetween className="mb-3">
                    <PageHeader header="Consolidated Report Emails" />
                    <AppPrimaryButton
                        onClick={() =>
                            navigate('/app/consolidated-report-email/new')
                        }
                    >
                        + New Schedule
                    </AppPrimaryButton>
                </FlexRowBetween>
            </DesktopMode>
            <MobileMode>
                <FlexColumn className="mb-3">
                    <PageHeader header="Consolidated Report Emails" />
                    <AppPrimaryButton
                        fluid
                        onClick={() =>
                            navigate('/app/consolidated-report-email/new')
                        }
                    >
                        + New Schedule
                    </AppPrimaryButton>
                </FlexColumn>
            </MobileMode>

            <SectionCard>
                <SectionHeader>
                    <div>
                        <h3>Run History — last {runsFilter.PageSize} fires</h3>
                        <p>Every scheduled or manual fire is logged here.</p>
                    </div>
                    <FilterRow>
                        <Dropdown
                            selection
                            placeholder="All schedules"
                            options={scheduleOptions}
                            value={runScheduleFilter}
                            onChange={(_, { value }) => {
                                setRunScheduleFilter(value)
                                runsFilterChanged({
                                    scheduleId:
                                        value && value !== 'all'
                                            ? value
                                            : undefined,
                                })
                            }}
                        />
                        <Dropdown
                            selection
                            placeholder="All statuses"
                            options={RUN_STATUS_OPTIONS}
                            value={runStatusFilter}
                            onChange={(_, { value }) => {
                                setRunStatusFilter(value)
                                runsFilterChanged({ status: value })
                            }}
                        />
                        <AppButton onClick={handleExportCsv}>Export CSV</AppButton>
                    </FilterRow>
                </SectionHeader>
                {(runsLoading || runsError) && (
                    <LoaderBar content={runsError || 'Loading run history'} />
                )}
                {!runsLoading && !filteredRuns.length && !runsError && (
                    <NoRunsEmpty nextFire={nextFire} />
                )}
                {filteredRuns.length > 0 && (
                    <RunHistoryList
                        data={filteredRuns}
                        filter={runsFilter}
                        pageChanged={runsPageChanged}
                        totalCount={filteredRunsCount}
                        timezone={timezone}
                        onRowClick={setSelectedRun}
                        onScheduleClick={item => {
                            if (!item?.ScheduleId) return
                            const stillExists = (schedules || []).some(
                                s => s.ScheduleId === item.ScheduleId,
                            )
                            if (!stillExists) {
                                ConsolidatedToasts.scheduleAlreadyDeleted()
                                return
                            }
                            navigate(
                                `/app/consolidated-report-email/${item.ScheduleId}/edit`,
                            )
                        }}
                    />
                )}
            </SectionCard>

            <SectionCard>
                <SectionHeader>
                    <div>
                        <h3>
                            Scheduled report digests
                            {totalCount ? ` — ${totalCount} schedules` : ''}
                        </h3>
                        <p>
                            Bundle completed audit reports on a recurring schedule
                            and email them to a chosen list of recipients.
                        </p>
                    </div>
                </SectionHeader>

                <FilterRow>
                    <Input
                        placeholder="Search by name..."
                        value={search}
                        onChange={(_, { value }) => setSearch(value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                e.preventDefault()
                                handleScheduleSearch()
                            }
                        }}
                        style={{ maxWidth: 280 }}
                    />
                    <Dropdown
                        selection
                        placeholder="All statuses"
                        options={STATUS_OPTIONS}
                        value={statusFilter}
                        onChange={(_, { value }) => setStatusFilter(value)}
                    />
                    <AppButton type="button" onClick={handleScheduleSearch}>
                        Search
                    </AppButton>
                    {(filter.search || filter.status !== 'all') && (
                        <AppButton type="button" onClick={handleClearScheduleSearch}>
                            Clear
                        </AppButton>
                    )}
                </FilterRow>

                {schedulesLoading && (
                    <LoaderBar content="Loading schedules" />
                )}
                {schedulesError && (
                    <div className="banner-error mb-2">
                        Couldn&apos;t load schedules — {schedulesError}.{' '}
                        <button
                            type="button"
                            className="ui button mini basic"
                            onClick={refreshSchedules}
                        >
                            Retry
                        </button>
                    </div>
                )}

                {!schedulesLoading && showFirstTimeEmpty && (
                    <NoSchedulesEmpty
                        onCreate={() =>
                            navigate('/app/consolidated-report-email/new')
                        }
                    />
                )}

                {!schedulesLoading && showSearchEmpty && (
                    <NoSearchResultsEmpty
                        query={filter.search || search}
                        onClear={handleClearScheduleSearch}
                    />
                )}

                {!schedulesLoading &&
                    !showFirstTimeEmpty &&
                    !showSearchEmpty &&
                    filteredSchedules.length > 0 && (
                        <ConsolidatedScheduleList
                            data={filteredSchedules}
                            filter={filter}
                            pageChanged={pageChanged}
                            totalCount={filteredScheduleCount}
                            timezone={timezone}
                            onEdit={item =>
                                navigate(
                                    `/app/consolidated-report-email/${item.ScheduleId}/edit`,
                                )
                            }
                            onHoldToggle={handleHoldToggle}
                            onFireNow={setFireNowSchedule}
                        />
                    )}
            </SectionCard>

            <FireNowModal
                open={Boolean(fireNowSchedule)}
                onClose={() => {
                    setFireNowSchedule(null)
                    setFireNowError('')
                }}
                onConfirm={handleFireNow}
                loading={fireNowLoading}
                schedule={fireNowSchedule}
                timezone={timezone}
                error={fireNowError}
            />

            <ViewRunDetailModal
                open={Boolean(selectedRun)}
                onClose={() => setSelectedRun(null)}
                run={selectedRun}
                timezone={timezone}
            />
        </ListScreenContainer>
    )
}
